// Paper.js (MIT) as a geometry library only (DRAW_PLAN G2): our subpaths ⇄ Paper paths for its curve
// intersection and boolean operations. Its scene graph and renderer are not used (items are never inserted).
import paper from 'paper/dist/paper-core';
import type { Anchor, Mat2D, Subpath } from '../model/types';
import { apply } from './mat';

let ready = false;
function ensure() {
  if (ready) return;
  paper.setup(new paper.Size(1, 1));
  paper.settings.insertItems = false;
  ready = true;
}

/** Subpaths (optionally mapped by `m`) as one Paper path item. */
export function toPaper(subpaths: Subpath[], m?: Mat2D): paper.PathItem {
  ensure();
  const map = (x: number, y: number) => (m ? apply(m, x, y) : [x, y]);
  const paths = subpaths
    .filter((sp) => sp.points.length >= 2)
    .map((sp) => {
      const segs = sp.points.map((p) => {
        const [x, y] = map(p.x, p.y);
        const [ix, iy] = map(p.inX, p.inY);
        const [ox, oy] = map(p.outX, p.outY);
        return new paper.Segment(
          new paper.Point(x!, y!),
          new paper.Point(ix! - x!, iy! - y!),
          new paper.Point(ox! - x!, oy! - y!),
        );
      });
      return new paper.Path({ segments: segs, closed: sp.closed, insert: false });
    });
  return paths.length === 1 ? paths[0]! : new paper.CompoundPath({ children: paths, insert: false });
}

/** A Paper path item back to subpaths (optionally mapped by `m`). */
export function fromPaper(item: paper.PathItem | paper.Item, m?: Mat2D): Subpath[] {
  const map = (x: number, y: number) => (m ? apply(m, x, y) : ([x, y] as [number, number]));
  const paths: paper.Path[] =
    item instanceof paper.CompoundPath ? (item.children as paper.Path[]) : [item as paper.Path];
  return paths
    .filter((p) => p.segments && p.segments.length >= 2)
    .map((p) => ({
      closed: p.closed,
      points: p.segments.map((s): Anchor => {
        const [x, y] = map(s.point.x, s.point.y);
        const [ix, iy] = map(s.point.x + s.handleIn.x, s.point.y + s.handleIn.y);
        const [ox, oy] = map(s.point.x + s.handleOut.x, s.point.y + s.handleOut.y);
        const smooth =
          !s.handleIn.isZero() &&
          !s.handleOut.isZero() &&
          Math.abs(s.handleIn.normalize().dot(s.handleOut.normalize()) + 1) < 1e-3;
        return { x: x!, y: y!, inX: ix!, inY: iy!, outX: ox!, outY: oy!, type: smooth ? 'smooth' : 'corner' };
      }),
    }));
}

/** Splits a compound result into islands: each outer contour with the holes inside it. */
export function islands(item: paper.PathItem): paper.PathItem[] {
  ensure();
  const paths: paper.Path[] =
    item instanceof paper.CompoundPath ? [...(item.children as paper.Path[])] : [item as paper.Path];
  if (paths.length <= 1) return paths;
  // A contour is a hole when an odd number of other contours contain it.
  const depth = paths.map((p) => paths.filter((q) => q !== p && q.contains(p.interiorPoint)).length);
  const outers = paths.filter((_, i) => depth[i]! % 2 === 0);
  return outers.map((o) => {
    const holes = paths.filter(
      (h, i) =>
        depth[i]! % 2 === 1 && o.contains(h.interiorPoint) && depth[i] === depth[paths.indexOf(o)]! + 1,
    );
    return holes.length
      ? new paper.CompoundPath({
          children: [o.clone({ insert: false }), ...holes.map((h) => h.clone({ insert: false }))],
          insert: false,
        })
      : o;
  });
}

/** Knife: cuts closed subpaths (world) along an open polyline (world). Returns the pieces, or null if no cut. */
export function knifeCut(target: Subpath[], line: [number, number][], m?: Mat2D): paper.PathItem[] | null {
  ensure();
  if (line.length < 2) return null;
  const shape = toPaper(target, m);
  // A ribbon 0.002 pt wide along the cut: subtracting it separates the pieces with an invisible gap.
  const e = 0.001;
  const left: paper.Point[] = [],
    right: paper.Point[] = [];
  line.forEach((p, i) => {
    const a = line[Math.max(0, i - 1)]!,
      b = line[Math.min(line.length - 1, i + 1)]!;
    const dx = b[0] - a[0],
      dy = b[1] - a[1];
    const l = Math.hypot(dx, dy) || 1;
    const nx = (-dy / l) * e,
      ny = (dx / l) * e;
    left.push(new paper.Point(p[0] + nx, p[1] + ny));
    right.unshift(new paper.Point(p[0] - nx, p[1] - ny));
  });
  const ribbon = new paper.Path({ segments: [...left, ...right], closed: true, insert: false });
  if (!shape.intersects(ribbon)) return null;
  const cut = shape.subtract(ribbon, { insert: false });
  const parts = islands(cut);
  return parts.length > 1 ? parts : null;
}
